/* eslint-disable react/jsx-wrap-multilines */
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';

import { HoldingClass, Share } from 'types';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

// import { getShareEncumbranceNames } from 'modules/partners/utils/getShareEncumbranceNames';
import { RowCell } from 'modules/_shared/Table/types/RowCell';
import DocumentsColumn from 'modules/_shared/components/DocumentsColumn';
import TableRow from 'modules/_shared/Table/components/TableRow';
import { useGetEncumbrances } from 'modules/partners/hooks/useGetEncumbrances';

import { useGetShareTitleData } from '../hooks/useGetShareTitleData';
import EncumbrancesTooltip from './EncumbrancesTooltip';
import {
  ShareTitleTooltipText,
  ShareTitleTooltipTextWithCoOwners,
} from '../../PartnerSociety/components/ShareTitleTooltip';
import { usePartnerShares } from '../context/PartnerSharesContext';

type PartnerSharesTableRowProps = {
  index: number;
  share: Share;
};

const PartnerSharesTableRow: FC<PartnerSharesTableRowProps> = ({
  index,
  share,
}) => {
  const { t } = useTranslate();
  const { data, actions } = usePartnerShares();

  const actualSociety = useSelector(
    (state: State) => state?.society?.actualSociety || {}
  );

  const isTitlesWithCoOwnersAvailable =
    !!data.societySharesTitleDocumentWithCoOwners &&
    data.hasSharesTitlesEnabled;

  const { data: encumbrancesData } = useGetEncumbrances({
    shareId: share?._id,
  });

  const { data: shareTitleData } = useGetShareTitleData({
    share,
    sharesTitles: data.sharesTitles,
    isAdmin: data.isAdmin,
  });

  const from = share?.from || 0;
  const to = share?.to || 0;
  const sharesCount = share?.to - share?.from + 1;
  const holdingClass = share?.shareClass as HoldingClass;
  const holdingClassName = holdingClass?.name || '';
  const nominalValue =
    holdingClass?.nominalValue ?? actualSociety?.nominalValue ?? 0;

  // const coOwnerSharesCount = getCoOwnerEncumberedSharesCount({ share });

  const isMainTitleAvailable = true;

  const handleClickRow = () => {
    actions.handleClickShare(index - 1);
  };

  const cells = [
    {
      key: `index-${index}`,
      content: <b>{index}</b>,
      className: 'w-10px',
      onClick: handleClickRow,
    },

    {
      key: `shares-count-${index}`,
      content: sharesCount ? formatNumber(sharesCount) : '-',
      textPosition: 'right',
      onClick: handleClickRow,
    },

    {
      key: `nominal-value-${index}`,
      content: formatCurrencyDecimals(nominalValue),
      textPosition: 'right',
      hideOnMobile: true,
      onClick: handleClickRow,
    },

    !data.hasPartnerSharesRangeDisabled && {
      key: `holding-class-name-${index}`,
      content: (
        <span className="w-100px d-block text-ellipsis">
          {holdingClassName}
        </span>
      ),
      hideOnMobile: true,
      onClick: handleClickRow,
    },

    !data.hasPartnerSharesRangeDisabled && {
      key: `from-${index}`,
      content: from ? formatNumber(from) : '-',
      textPosition: 'right',
      onClick: handleClickRow,
    },

    !data.hasPartnerSharesRangeDisabled && {
      key: `to-${index}`,
      content: to ? formatNumber(to) : '-',
      textPosition: 'right',
      onClick: handleClickRow,
    },

    !data.hasPartnerSharesRangeDisabled &&
      data.isAdmin && {
        key: `encumbrances-${index}`,
        content: (
          <EncumbrancesTooltip
            shareId={share?._id}
            encumbrances={encumbrancesData?.encumbrances || []}
          />
        ),
        textPosition: 'center',
        hideOnMobile: true,
        onClick: handleClickRow,
      },

    !data.hasPartnerSharesRangeDisabled &&
      data.showSharesTitleColumn && {
        key: `document-${index}`,
        content: shareTitleData.titleId ? (
          <td className="p-0">
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {isMainTitleAvailable && (
                <DocumentsColumn
                  index={index}
                  documentsCount={1}
                  documents={
                    data.societyShareTitleDocument
                      ? [data.societyShareTitleDocument]
                      : []
                  }
                  downloadName={shareTitleData.titleName}
                  hideOnMobile={false}
                  sources={{
                    society: data.societyId,
                    sharesTitle: shareTitleData.titleId,
                  }}
                  templateWatermarkText={shareTitleData.titleWatermarkText}
                  templateTextPadding={data.societyShareTitleTextPadding}
                  isLandscape={data.isSocietyShareTitleLandscape}
                  tooltipId={`share-title-tooltip-${index}`}
                  tooltipText={
                    <ShareTitleTooltipText
                      titleNumber={shareTitleData.titleNumber}
                    />
                  }
                  tooltipPlace="left"
                  tableView={false}
                />
              )}

              {isTitlesWithCoOwnersAvailable && (
                <DocumentsColumn
                  index={index}
                  documentsCount={1}
                  documents={
                    data.societySharesTitleDocumentWithCoOwners
                      ? [data.societySharesTitleDocumentWithCoOwners]
                      : []
                  }
                  downloadName={shareTitleData.titleCoOwnerName}
                  hideOnMobile={false}
                  sources={{
                    society: data.societyId,
                    sharesTitle: shareTitleData.titleId,
                  }}
                  templateWatermarkText={shareTitleData.titleWatermarkText}
                  templateTextPadding={data.societyShareTitleTextPadding}
                  isLandscape={data.isSocietyShareTitleLandscape}
                  tooltipId={`share-title-with-co-owners-tooltip-${index}`}
                  tooltipText={
                    <ShareTitleTooltipTextWithCoOwners
                      encumbranceNumber={shareTitleData.titleEncumbranceNumber}
                    />
                  }
                  tooltipPlace="left"
                  tableView={false}
                />
              )}
            </div>
          </td>
        ) : (
          '-'
        ),
        documentCell: !!shareTitleData.titleId,
        textPosition: 'center',
      },
  ].filter(Boolean) as RowCell[];

  return <TableRow cells={cells} />;
};
export default PartnerSharesTableRow;
